import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  displaySDFInfo,
  displaySDFTitleInfo,
  findUnitCodeAndName,
  paintJustUnitScopes,
  paintScopeDesc
} from './UtilFunctions';
import { SCOPE_LEVEL_UNITORUNITGROUP } from './AppConstants';

export default function ScopeViewOnly(props) {
  const { getMissingSDFDisplay, strategyInformation } = props;

  const selectedData = props.selectedScopeViewList
    ? props.selectedScopeViewList
    : strategyInformation?.scope;
  if (!selectedData) {
    return null;
  }

  const masterData = props.masterData;
  const selectedScopeUnits = props.selectedScopeUnits;
  const editScope = props.editScope;
  const isEdit = props.isEdit;
  const hasUSScope = props.hasUSScope;

  /**
   * The master scope payload exposes unit groups per global level
   * (globalLevelMap); a flat unitGroups array is only present in older shapes.
   */
  function getMasterUnitGroups(master) {
    if (Array.isArray(master?.unitGroups) && master.unitGroups.length > 0) {
      return master.unitGroups;
    }

    const globalLevelMap = master?.globalLevelMap;
    if (!globalLevelMap) {
      return [];
    }

    const uniqueGroups = new Map();

    Object.values(globalLevelMap).forEach((groups) => {
      (groups || []).forEach((group) => {
        if (group?.code && !uniqueGroups.has(group.code)) {
          uniqueGroups.set(group.code, group);
        }
      });
    });

    return Array.from(uniqueGroups.values());
  }

  // A unit's parent is a unit group object in the API payload, but older
  // payloads store the group code directly.
  function getUnitParentCode(unitMetaObj) {
    const parent = unitMetaObj?.parent;

    return typeof parent === 'string' ? parent : parent?.code;
  }

  function findSelectedScopeGroups(masterUnitMap, selectedScope) {
    let map = new Map(); // Key is Group Code and Value is Map of Unit Codes

    if (selectedScope) {
      // Add Groups
      Array.isArray(selectedScope.unitGroups) &&
        selectedScope.unitGroups.forEach((groupCode) =>
          map.set(groupCode, null)
        );

      // Add Units and their Groups
      masterUnitMap &&
        Array.isArray(selectedScope.units) &&
        selectedScope.units.forEach((unitCode) => {
          // Find Unit Meta Info
          let unitMetaObj = masterUnitMap.get(unitCode);
          const parentCode = getUnitParentCode(unitMetaObj);
          if (!parentCode) {
            return;
          }

          // Check if the Group already has a Unit map associated!
          let childMap = map.get(parentCode);

          if (!childMap) {
            childMap = new Map();
            // Add to Parent (Group) map - value (Child/Unit Map)
            map.set(parentCode, childMap);
          }

          // Add to childMap of Group
          childMap.set(unitCode, unitCode);
        });
    }
    return map;
  }

  function paintSelectedAndNoTSelectedChildren(
    children,
    selectedChildrenMap,
    showAsInactive,
    condition
  ) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {children.map(
          (child) =>
            selectedChildrenMap &&
            selectedChildrenMap.has(child.code) === condition && (
              <Box
                key={child.code}
                sx={showAsInactive ? { color: 'text.disabled' } : {}}
              >
                {paintCodeDescInternal(child, showAsInactive, 3)}
              </Box>
            )
        )}
      </Box>
    );
  }
  function paintCodeDescInternal(metaObj, showAsInactive, paddingLeft) {
    return (
      <Box key={metaObj.code} sx={{ paddingLeft: paddingLeft }}>
        {paintScopeDesc(metaObj.code, metaObj.name, showAsInactive)}
      </Box>
    );
  }
  function paintMinor(
    idPropName,
    metaObj,
    childrenPropName,
    precedingCode,
    precedingName,
    paddingLeft,
    defaultExpanded,
    selectedChildrenMap
  ) {
    let children = metaObj[childrenPropName];
    let hasChildren = children && children.length > 0;
    let currentObjCode = precedingCode;
    let currentObjName = precedingName;
    let styleObj = {
      margin: 0,
      padding: 0,
      minHeight: 0
    };

    return hasChildren ? (
      <Accordion
        key={idPropName + metaObj.code}
        id={idPropName + metaObj.code}
        defaultExpanded={defaultExpanded}
        variant="outlined"
        sx={{
          border: 'none',
          ...styleObj
        }}
      >
        <AccordionSummary
          expandIcon={<fuel-icon name="keyboard_arrow_down" />}
          sx={styleObj}
        >
          {paintScopeDesc(
            currentObjCode,
            currentObjName,
            false,
            selectedChildrenMap &&
              `${selectedChildrenMap.size}/${children.length} Units Included`,
            true
          )}
        </AccordionSummary>
        <AccordionDetails>
          {selectedChildrenMap ? (
            <>
              <Typography sx={{ fontWeight: 'bold' }}>
                Included in Strategy
              </Typography>
              {paintSelectedAndNoTSelectedChildren(
                children,
                selectedChildrenMap,
                false,
                true
              )}
              <hr />
              <Typography sx={{ fontWeight: 'bold' }}>
                Not Included in Strategy
              </Typography>
              {paintSelectedAndNoTSelectedChildren(
                children,
                selectedChildrenMap,
                true,
                false
              )}
              <hr />
            </>
          ) : (
            children.map((child) =>
              paintMinor(
                metaObj.code + '_unit',
                child,
                null,
                currentObjCode,
                currentObjName,
                3
              )
            )
          )}
        </AccordionDetails>
      </Accordion>
    ) : (
      paintCodeDescInternal(metaObj, false, paddingLeft)
    );
  }

  // Component Painting

  let masterUnitMap;

  // Prepare Master Unit Map iff Units are selected
  if (
    Array.isArray(selectedData.units) &&
    selectedData.units.length > 0 &&
    masterData?.unitsMap
  ) {
    masterUnitMap = new Map(Object.entries(masterData.unitsMap));
  }

  let selectedGroupMap =
    selectedData.level === SCOPE_LEVEL_UNITORUNITGROUP &&
    findSelectedScopeGroups(masterUnitMap, selectedData);

  const masterUnitGroups = getMasterUnitGroups(masterData);

  let showAtUnitLevel = false;

  selectedGroupMap &&
    selectedGroupMap.size === 1 &&
    selectedGroupMap.forEach((value) => {
      showAtUnitLevel = value && value.size < 6;
    });

  return (
    <Box sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingMedium })}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            {isEdit && (
              <IconButton
                aria-label="edit"
                onClick={(e) => editScope(e)}
                sx={{ borderRadius: 1, flexShrink: 0 }}
              >
                <fuel-icon name="edit" />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              {/* Body — vertical stack */}
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.fuel6Tokens.JddcSpacingMedium
                })}
              >
                {/* Scope content */}
                <Box>
                  <Typography variant="h3">Scope</Typography>
                  {selectedData.level === SCOPE_LEVEL_UNITORUNITGROUP ? (
                    showAtUnitLevel ? (
                      props.selectedScopeViewList && selectedScopeUnits ? (
                        paintJustUnitScopes(
                          findUnitCodeAndName(selectedScopeUnits)
                        )
                      ) : (
                        paintJustUnitScopes(
                          strategyInformation.decorated?.scope?.units
                        )
                      )
                    ) : (
                      <>
                        {masterUnitGroups.map((metaGroup) =>
                          selectedGroupMap?.has(metaGroup.code)
                            ? paintMinor(
                                'group',
                                metaGroup,
                                'units',
                                metaGroup.code,
                                metaGroup.name,
                                9,
                                false,
                                selectedGroupMap.get(metaGroup.code)
                              )
                            : null
                        )}
                      </>
                    )
                  ) : (
                    <Typography>{selectedData.level}</Typography>
                  )}
                </Box>

                {/* Small Business Form */}
                {strategyInformation && (
                  <Box>
                    {displaySDFTitleInfo(hasUSScope, strategyInformation)}
                    {displaySDFInfo(
                      props.hasUSScope,
                      strategyInformation,
                      false,
                      isEdit,
                      getMissingSDFDisplay
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
